import { Loader2, X } from 'lucide-react';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
  /** While true the action is in flight: both buttons disable and confirm shows a spinner. */
  busy?: boolean;
  /** Optional error message shown inline when the confirmed action fails. */
  error?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  busy = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="animate-in fade-in zoom-in bg-surface w-full max-w-sm overflow-hidden rounded-2xl shadow-xl duration-200">
        <div className="border-subtle flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-fg text-lg font-bold">{title}</h3>
          <button
            onClick={onCancel}
            disabled={busy}
            className="hover:bg-muted rounded-full p-2 transition-colors disabled:pointer-events-none disabled:opacity-40"
          >
            <X className="text-fg-subtle h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3 px-6 py-4">
          <p className="text-fg-secondary text-sm">{message}</p>
          {error && (
            <div className="border-danger/25 bg-danger-soft text-danger rounded-lg border px-3 py-2 text-sm">
              {error}
            </div>
          )}
        </div>
        <div className="border-subtle flex gap-3 border-t px-6 py-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="border-default text-fg-secondary hover:bg-canvas flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              variant === 'danger'
                ? 'bg-danger-solid hover:bg-danger-solid-hover'
                : 'bg-brand-600 hover:bg-brand-700'
            }`}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
